import { hashString, seededRandom } from "../core/random";
import type { ArchitectureKind } from "./ArchitectureLibrary";
import type { ChunkBounds } from "./geometry";
import type { CardinalAxis, JiangnanWorldModel } from "./model";

export interface AmbientBoatPlacement {
  axis: CardinalAxis;
  range: number;
  speed: number;
  phase: number;
}

export interface ArchitecturePlacement {
  kind: ArchitectureKind;
  x: number;
  y: number;
  z: number;
  rotation: number;
  scale: readonly [number, number, number];
  boat?: AmbientBoatPlacement;
  node?: {
    id: string;
    kind: "bridge-quay" | "pocket-pavilion" | "pocket-life";
    role: "bridge" | "quay" | "pavilion" | "yard-edge" | "life-detail";
    opening?: readonly [number, number];
  };
}

const HOUSE_VARIANTS = ["canal-house", "canal-house-balcony", "canal-house-hipped"] as const;
const QUAY_LIFE = ["life-laundry", "life-repair"] as const;
const STREET_LIFE = ["life-rain-gear", "life-scooter", "life-utility"] as const;
const SQUARE_LIFE = ["life-breakfast", "life-laundry", "life-rain-gear"] as const;

const DIRECTIONS = [
  { x: 0, z: 1, rotation: 0 },
  { x: 1, z: 0, rotation: Math.PI * 0.5 },
  { x: 0, z: -1, rotation: Math.PI },
  { x: -1, z: 0, rotation: -Math.PI * 0.5 },
] as const;

export function createWaterTownPlacements(
  model: JiangnanWorldModel,
  bounds: ChunkBounds,
  chunkX: number,
  chunkZ: number,
  detail: "near" | "far",
): ArchitecturePlacement[] {
  const placements: ArchitecturePlacement[] = [];
  if (contains(bounds, 0, 8)) placements.push(uniform("home", 0, model.getHeight(0, 8), 8, 1, 0));

  for (const bridge of model.getBridgesInBounds(bounds)) {
    if (!contains(bounds, bridge.x, bridge.z)) continue;
    const verticalScale = 0.36;
    const nodeId = `bridge:${bridge.id}`;
    placements.push({
      kind: "stone-bridge",
      x: bridge.x,
      y: bridge.deckHeight - 1.46 * verticalScale,
      z: bridge.z,
      rotation: bridge.axis === "x" ? Math.PI * 0.5 : 0,
      scale: [bridge.width / 4.9, verticalScale, bridge.length / 13.2],
      node: { id: nodeId, kind: "bridge-quay", role: "bridge" },
    });
    if (detail === "near") addBridgeQuayNode(placements, model, bounds, bridge, nodeId);
  }

  addBuildingPlots(placements, model, bounds, chunkX, chunkZ, detail);
  if (detail === "near") {
    addWatersideDetails(placements, model, bounds, chunkX, chunkZ);
    addAmbientBoat(placements, model, bounds, chunkX, chunkZ);
  }
  return placements;
}

function addBuildingPlots(
  placements: ArchitecturePlacement[],
  model: JiangnanWorldModel,
  bounds: ChunkBounds,
  chunkX: number,
  chunkZ: number,
  detail: "near" | "far",
): void {
  const spacing = detail === "near" ? 9.5 : 18;
  const minCellX = Math.floor(bounds.minX / spacing);
  const maxCellX = Math.ceil((bounds.minX + bounds.size) / spacing);
  const minCellZ = Math.floor(bounds.minZ / spacing);
  const maxCellZ = Math.ceil((bounds.minZ + bounds.size) / spacing);
  for (let cellZ = minCellZ; cellZ <= maxCellZ; cellZ += 1) {
    for (let cellX = minCellX; cellX <= maxCellX; cellX += 1) {
      const random = seededRandom(`${model.version}:${model.seed}:building:${cellX}:${cellZ}`);
      const jitter = detail === "near" ? 0.8 : 2;
      const rowOffset = detail === "near"
        ? (hashString(`${model.version}:${model.seed}:building-row:${cellZ}`) / 0xffffffff - 0.5) * 1.6
        : 0;
      const x = (cellX + 0.5) * spacing + rowOffset + (random() - 0.5) * jitter;
      const z = (cellZ + 0.5) * spacing + (random() - 0.5) * jitter;
      if (!contains(bounds, x, z) || Math.hypot(x, z - 8) < 19) continue;
      const semantic = model.getTownSemantic(x, z);
      const rotation = facingWaterOrWalkway(model, x, z);
      const houseChance = detail === "near" ? 0.9 : 0.58;
      if (semantic.feature !== "building-plot" || random() > houseChance) {
        const enclosureChance = semantic.feature === "garden"
          ? detail === "near" ? 0.32 : 0.16
          : detail === "near" ? 0.62 : 0.26;
        if (random() <= enclosureChance && enclosureFits(model, x, z, rotation)) {
          addEnclosure(placements, model, random, x, z, rotation);
        }
        continue;
      }
      if (!footprintFits(model, x, z, rotation)) {
        if (detail === "near" && enclosureFits(model, x, z, rotation)) {
          addEnclosure(placements, model, random, x, z, rotation);
        }
        continue;
      }
      const variantIndex = hashString(`${model.version}:${model.seed}:house-variant:${cellX}:${cellZ}`) % HOUSE_VARIANTS.length;
      const kind: ArchitectureKind = detail === "near" && hashString(`${model.seed}:tea:${cellX}:${cellZ}`) % 19 === 0
        ? "tea-room"
        : HOUSE_VARIANTS[variantIndex]!;
      if (kind === "tea-room") {
        placements.push(uniform(kind, x, model.getHeight(x, z), z, 0.92, rotation));
      } else {
        const width = 0.84 + random() * 0.14;
        const height = 0.8 + random() * 0.3;
        const depth = 0.82 + random() * 0.1;
        placements.push({ kind, x, y: model.getHeight(x, z), z, rotation, scale: [width, height, depth] });
      }
    }
  }
}

function addEnclosure(
  placements: ArchitecturePlacement[],
  model: JiangnanWorldModel,
  random: () => number,
  x: number,
  z: number,
  rotation: number,
): void {
  const width = 0.86 + random() * 0.12;
  const height = 0.82 + random() * 0.16;
  const depth = 0.84 + random() * 0.12;
  placements.push({
    kind: "courtyard-enclosure",
    x,
    y: model.getHeight(x, z),
    z,
    rotation,
    scale: [width, height, depth],
  });
}

function enclosureFits(model: JiangnanWorldModel, x: number, z: number, rotation: number): boolean {
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  for (const localX of [-3.7, 0, 3.7]) {
    for (const localZ of [-2.7, 0, 2.7]) {
      const sampleX = x + localX * cos - localZ * sin;
      const sampleZ = z + localX * sin + localZ * cos;
      const feature = model.getTownFeature(sampleX, sampleZ);
      if (feature !== "building-plot" && feature !== "garden") return false;
    }
  }
  return true;
}

function footprintFits(model: JiangnanWorldModel, x: number, z: number, rotation: number): boolean {
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  let gardenSamples = 0;
  for (const localX of [-2.8, 0, 2.8]) {
    for (const localZ of [-4.5, 0, 4.5]) {
      const sampleX = x + localX * cos - localZ * sin;
      const sampleZ = z + localX * sin + localZ * cos;
      const feature = model.getTownFeature(sampleX, sampleZ);
      if (feature !== "building-plot" && feature !== "garden") return false;
      if (feature === "garden") gardenSamples += 1;
    }
  }
  return gardenSamples <= 2;
}

function addWatersideDetails(
  placements: ArchitecturePlacement[],
  model: JiangnanWorldModel,
  bounds: ChunkBounds,
  chunkX: number,
  chunkZ: number,
): void {
  const random = seededRandom(`${model.version}:${model.seed}:waterside:${chunkX}:${chunkZ}`);
  const candidates = 18;
  let galleryAdded = false;
  let quayAdded = false;
  let streetLifeAdded = false;
  for (let index = 0; index < candidates; index += 1) {
    const x = bounds.minX + 4 + random() * (bounds.size - 8);
    const z = bounds.minZ + 4 + random() * (bounds.size - 8);
    const feature = model.getTownFeature(x, z);
    if (!galleryAdded && feature === "canal-street") {
      const rotation = walkwayAxis(model, x, z) === "x" ? 0 : Math.PI * 0.5;
      placements.push(uniform("covered-gallery", x, model.getHeight(x, z), z, 0.92, rotation));
      galleryAdded = true;
    } else if (!quayAdded && feature === "quay") {
      placements.push(uniform("quay-dock", x, model.getHeight(x, z), z, 0.92, facingWater(model, x, z)));
      quayAdded = true;
    } else if (!streetLifeAdded && feature === "canal-street" && random() < 0.34) {
      const nodeId = `street:${chunkX}:${chunkZ}:${Math.round(x)}:${Math.round(z)}`;
      placements.push({
        ...uniform(pick(STREET_LIFE, `${model.version}:${model.seed}:${nodeId}:life`), x, model.getHeight(x, z), z, 0.82, facingWater(model, x, z) + Math.PI),
        node: { id: nodeId, kind: "pocket-life", role: "life-detail" },
      });
      streetLifeAdded = true;
    }
    if (galleryAdded && quayAdded && streetLifeAdded) break;
  }
  addPocketNode(placements, model, bounds, chunkX, chunkZ);
}

function addPocketNode(
  placements: ArchitecturePlacement[],
  model: JiangnanWorldModel,
  bounds: ChunkBounds,
  chunkX: number,
  chunkZ: number,
): void {
  const phase = hashString(`${model.version}:${model.seed}:pocket-phase:${chunkX}:${chunkZ}`) % 3;
  let square: { x: number; z: number } | null = null;
  for (let localZ = 2 + phase; localZ < bounds.size - 2; localZ += 3) {
    for (let localX = 2 + phase; localX < bounds.size - 2; localX += 3) {
      const x = bounds.minX + localX;
      const z = bounds.minZ + localZ;
      if (model.getTownFeature(x, z) !== "square") continue;
      square ??= { x, z };
      const center = findPavilionCenter(model, bounds, x, z);
      if (!center || !contains(bounds, center.x, center.z)) continue;
      const nodeId = `pocket:${chunkX}:${chunkZ}:${Math.round(center.x)}:${Math.round(center.z)}`;
      const opening = pocketOpeningDirection(model, center.x, center.z, nodeId);
      placements.push({
        ...uniform("pavilion", center.x, model.getHeight(center.x, center.z), center.z, 0.62, quarterTurnFromSeed(`${nodeId}:pavilion`)),
        node: { id: nodeId, kind: "pocket-pavilion", role: "pavilion", opening: [opening.x, opening.z] },
      });
      addPocketRichness(placements, model, bounds, center, nodeId, "pocket-pavilion", opening);
      return;
    }
  }
  if (square) {
    const nodeId = `pocket:${chunkX}:${chunkZ}:${Math.round(square.x)}:${Math.round(square.z)}`;
    const opening = pocketOpeningDirection(model, square.x, square.z, nodeId);
    addPocketRichness(placements, model, bounds, square, nodeId, "pocket-life", opening);
  }
}

function addPocketRichness(
  placements: ArchitecturePlacement[],
  model: JiangnanWorldModel,
  bounds: ChunkBounds,
  center: { x: number; z: number },
  nodeId: string,
  nodeKind: "pocket-pavilion" | "pocket-life",
  opening: (typeof DIRECTIONS)[number],
): void {
  const openingIndex = DIRECTIONS.indexOf(opening);
  const node = { id: nodeId, kind: nodeKind, opening: [opening.x, opening.z] as const };
  for (const offset of [-1, 1]) {
    const direction = DIRECTIONS[(openingIndex + offset + DIRECTIONS.length) % DIRECTIONS.length]!;
    const edgeX = center.x + direction.x * 4.12;
    const edgeZ = center.z + direction.z * 4.12;
    if (!contains(bounds, edgeX, edgeZ) || !model.isWalkable(edgeX, edgeZ)) continue;
    placements.push({
      ...uniform("pocket-yard-edge", edgeX, model.getHeight(edgeX, edgeZ), edgeZ, 0.7, direction.rotation),
      node: { ...node, role: "yard-edge" },
    });
  }

  const rearX = center.x - opening.x * 3.48;
  const rearZ = center.z - opening.z * 3.48;
  if (contains(bounds, rearX, rearZ) && model.isWalkable(rearX, rearZ)) {
    placements.push({
      ...uniform(pick(SQUARE_LIFE, `${model.version}:${model.seed}:${nodeId}:life`), rearX, model.getHeight(rearX, rearZ), rearZ, 0.76, opening.rotation),
      node: { ...node, role: "life-detail" },
    });
  }
}

function addBridgeQuayNode(
  placements: ArchitecturePlacement[],
  model: JiangnanWorldModel,
  bounds: ChunkBounds,
  bridge: ReturnType<JiangnanWorldModel["getBridgesInBounds"]>[number],
  nodeId: string,
): void {
  if (hashString(`${model.version}:${model.seed}:${nodeId}:detail`) % 3 === 0) return;
  const along = bridge.axis === "x" ? { x: 1, z: 0 } : { x: 0, z: 1 };
  const bank = bridge.axis === "x" ? { x: 0, z: 1 } : { x: 1, z: 0 };
  const sign = hashString(`${model.version}:${model.seed}:${nodeId}:side`) % 2 === 0 ? -1 : 1;
  const approaches = [sign, -sign];
  for (const approach of approaches) {
    for (let approachOffset = -0.5; approachOffset <= 4.5; approachOffset += 0.5) {
      const landingDistance = bridge.length * 0.5 + approachOffset;
      for (let lateral = -9; lateral <= 9; lateral += 1.5) {
        const x = bridge.x + along.x * landingDistance * approach + bank.x * lateral;
        const z = bridge.z + along.z * landingDistance * approach + bank.z * lateral;
        if (!contains(bounds, x, z) || model.getTownFeature(x, z) !== "quay") continue;
        placements.push({
          ...uniform("quay-dock", x, model.getHeight(x, z), z, 0.82, facingWater(model, x, z)),
          node: { id: nodeId, kind: "bridge-quay", role: "quay" },
        });
        const lifeX = x + bank.x * (lateral > 0 ? -3.8 : 3.8);
        const lifeZ = z + bank.z * (lateral > 0 ? -3.8 : 3.8);
        if (contains(bounds, lifeX, lifeZ) && model.isWalkable(lifeX, lifeZ)) {
          placements.push({
            ...uniform(pick(QUAY_LIFE, `${model.version}:${model.seed}:${nodeId}:life`), lifeX, model.getHeight(lifeX, lifeZ), lifeZ, 0.8, facingWater(model, lifeX, lifeZ) + Math.PI),
            node: { id: nodeId, kind: "bridge-quay", role: "life-detail" },
          });
        }
        return;
      }
    }
  }
}

function pavilionFits(model: JiangnanWorldModel, x: number, z: number): boolean {
  const corners: ReadonlyArray<readonly [number, number]> = [[-1.55, -1.55], [-1.55, 1.55], [1.55, -1.55], [1.55, 1.55]];
  return corners.every(
    ([offsetX, offsetZ]) => model.isWalkable(x + offsetX, z + offsetZ),
  );
}

function findPavilionCenter(model: JiangnanWorldModel, bounds: ChunkBounds, x: number, z: number): { x: number; z: number } | null {
  const offsets = [0, -1.5, 1.5, -3, 3];
  for (const offsetZ of offsets) {
    for (const offsetX of offsets) {
      const candidateX = x + offsetX;
      const candidateZ = z + offsetZ;
      const hasRichnessMargin = DIRECTIONS.every((direction) => contains(bounds, candidateX + direction.x * 4.12, candidateZ + direction.z * 4.12));
      if (hasRichnessMargin && model.getTownFeature(candidateX, candidateZ) === "square" && pavilionFits(model, candidateX, candidateZ)) {
        return { x: candidateX, z: candidateZ };
      }
    }
  }
  return null;
}

function pocketOpeningDirection(
  model: JiangnanWorldModel,
  x: number,
  z: number,
  nodeId: string,
): (typeof DIRECTIONS)[number] {
  const candidates = DIRECTIONS.filter((direction) => [2.6, 4.4, 6].every(
    (distance) => model.isWalkable(x + direction.x * distance, z + direction.z * distance),
  ));
  const options = candidates.length > 0 ? candidates : DIRECTIONS;
  return [...options].sort((a, b) => {
    const scoreDifference = pocketOpeningScore(model, x, z, b) - pocketOpeningScore(model, x, z, a);
    if (scoreDifference !== 0) return scoreDifference;
    return hashString(`${model.version}:${model.seed}:${nodeId}:opening:${a.x}:${a.z}`)
      - hashString(`${model.version}:${model.seed}:${nodeId}:opening:${b.x}:${b.z}`);
  })[0]!;
}

function pocketOpeningScore(
  model: JiangnanWorldModel,
  x: number,
  z: number,
  direction: (typeof DIRECTIONS)[number],
): number {
  for (let distance = 7; distance <= 36; distance += 1) {
    const sampleX = x + direction.x * distance;
    const sampleZ = z + direction.z * distance;
    if (model.getBridgeAt(sampleX, sampleZ)) return 100 - distance;
    if (model.getWaterDepth(sampleX, sampleZ) > 0) return 72 - distance;
  }
  return 0;
}

function pick<T>(values: readonly T[], seed: string): T {
  return values[hashString(seed) % values.length]!;
}

function quarterTurnFromSeed(seed: string): number {
  return (hashString(seed) % 4) * Math.PI * 0.5;
}

function addAmbientBoat(
  placements: ArchitecturePlacement[],
  model: JiangnanWorldModel,
  bounds: ChunkBounds,
  chunkX: number,
  chunkZ: number,
): void {
  if (hashString(`${model.version}:${model.seed}:boat:${chunkX}:${chunkZ}`) % 3 !== 0) return;
  const random = seededRandom(`${model.version}:${model.seed}:boat-point:${chunkX}:${chunkZ}`);
  for (let index = 0; index < 28; index += 1) {
    const x = bounds.minX + 7 + random() * (bounds.size - 14);
    const z = bounds.minZ + 7 + random() * (bounds.size - 14);
    const canal = model.getCanalAt(x, z);
    if (!canal || model.getBridgeAt(x, z)) continue;
    placements.push({
      kind: "wupeng-boat",
      x,
      y: canal.waterHeight - 0.25,
      z,
      rotation: canal.axis === "x" ? Math.PI * 0.5 : 0,
      scale: [0.92, 0.92, 0.92],
      boat: { axis: canal.axis, range: 4.5, speed: 0.22 + random() * 0.09, phase: random() * Math.PI * 2 },
    });
    return;
  }
}

function facingWalkway(model: JiangnanWorldModel, x: number, z: number, distance: number): number {
  return DIRECTIONS.find((direction) => model.isWalkable(x + direction.x * distance, z + direction.z * distance))?.rotation
    ?? (hashString(`${model.seed}:facing:${Math.round(x)}:${Math.round(z)}`) % 4) * Math.PI * 0.5;
}

function facingWaterOrWalkway(model: JiangnanWorldModel, x: number, z: number): number {
  for (const distance of [6.2, 7.4, 8.6]) {
    const direction = DIRECTIONS.find((candidate) => model.getWaterDepth(x + candidate.x * distance, z + candidate.z * distance) > 0);
    if (direction) return direction.rotation;
  }
  return facingWalkway(model, x, z, 6.2);
}

function facingWater(model: JiangnanWorldModel, x: number, z: number): number {
  return DIRECTIONS.find((direction) => model.getWaterDepth(x + direction.x * 4.5, z + direction.z * 4.5) > 0)?.rotation ?? 0;
}

function walkwayAxis(model: JiangnanWorldModel, x: number, z: number): CardinalAxis {
  const xScore = Number(model.isWalkable(x - 5, z)) + Number(model.isWalkable(x + 5, z));
  const zScore = Number(model.isWalkable(x, z - 5)) + Number(model.isWalkable(x, z + 5));
  return xScore >= zScore ? "x" : "z";
}

function uniform(
  kind: ArchitectureKind,
  x: number,
  y: number,
  z: number,
  scale: number,
  rotation: number,
): ArchitecturePlacement {
  return { kind, x, y, z, rotation, scale: [scale, scale, scale] };
}

function contains(bounds: ChunkBounds, x: number, z: number): boolean {
  return x >= bounds.minX && x < bounds.minX + bounds.size && z >= bounds.minZ && z < bounds.minZ + bounds.size;
}
